import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, type SignerResponseDto } from 'xosign-types';
import useSign from '@/pages/SignLayout.context';
import useApi from '@/hooks/useApi';
import Button from '@/components/Button';
import FormError from '@/components/FormError';
import FormInput from '@/components/FormInput';
import FormPhoneInput from '@/components/FormPhoneInput';
import { isValidEmail } from '@/utils/validation';
import IcCross from '@/assets/icons/Cross.svg?react';
import IcInfoCircled from '@/assets/icons/InfoCircled.svg?react';
import IcPlus from '@/assets/icons/Plus.svg?react';
import LoginGuardShell from '../Login/LoginGuardShell';
import styles from './Representatives.module.scss';

type RepRow = {
  name: string;
  email: string;
  phone: string;
};

const EMPTY_ROW: RepRow = { name: '', email: '', phone: '' };

const DEFAULT_CAP = 3;

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
};

const Representatives = () => {
  const { t } = useTranslation(['sign', 'common', 'auth']);
  const { workflowId } = useParams();
  const { signing, signers, visitor, loadAllData } = useSign();

  const [rows, setRows] = useState<RepRow[]>([{ ...EMPTY_ROW }]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submit, { loading }] = useApi('sign.representatives.submit');

  const cap = signing?.companyRepresentativesCap ?? DEFAULT_CAP;
  const phoneRequired = signing?.enhancedSecurity ?? false;
  // AES signers use SMS verification when a phone is known — collect it for
  // ADVANCED signings too, required only under enhanced security.
  const showPhone = phoneRequired || signing?.signatureType === 'ADVANCED';
  const companyName = visitor?.name ?? '';
  const named = signers.filter(
    (signer) => signer.representativeOf === visitor?.id
  );
  const myselfAdded = rows.some(
    (row) => row.email.toLowerCase() === (visitor?.email ?? '').toLowerCase()
  );

  if (named.length > 0) {
    return <Submitted companyName={companyName} named={named} />;
  }

  const updateRow = (index: number, patch: Partial<RepRow>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const addMyself = () => {
    setRows((prev) => {
      const filled = prev.filter((row) => row.name || row.email);
      return [...filled, { ...EMPTY_ROW, email: visitor?.email ?? '' }];
    });
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): string | null => {
    const incomplete = rows.some(
      (row) => !row.name.trim() || !isValidEmail(row.email, true)
    );
    if (incomplete) {
      return t('sign:representatives.errors.incomplete');
    }

    const emails = rows.map((row) => row.email.toLowerCase());
    if (new Set(emails).size !== emails.length) {
      return t('sign:representatives.errors.ERepresentativeDuplicateEmail');
    }

    if (phoneRequired && rows.some((row) => !row.phone.trim())) {
      return t('sign:representatives.errors.ERepresentativePhoneRequired');
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    setFormError(validationError);
    if (validationError) {
      return;
    }

    const result = await submit({
      workflowId: workflowId ?? '',
      representatives: rows.map((row) => ({
        name: row.name.trim(),
        email: row.email.trim(),
        ...(row.phone.trim() ? { phone: row.phone.trim() } : {}),
      })),
    });

    if (result?.response?.success) {
      void loadAllData();
      return;
    }

    const error = result?.error;
    const code = error && 'code' in error ? error.code : null;
    const known =
      code === ErrorCodes.ERepresentativesAlreadyNamed ||
      code === ErrorCodes.ERepresentativesCapExceeded ||
      code === ErrorCodes.ERepresentativeDuplicateEmail ||
      code === ErrorCodes.ERepresentativePhoneRequired;
    setFormError(
      known
        ? t(`sign:representatives.errors.${code}`)
        : t('sign:representatives.errors.generic')
    );
  };

  return (
    <LoginGuardShell
      introKey="sign:representatives.intro"
      titleKey="sign:representatives.title"
    >
      <div className={styles.body}>
        <div className={styles.callout}>
          <IcInfoCircled className={styles.calloutIcon} aria-hidden="true" />
          <div>
            <div>{t('sign:representatives.consequence')}</div>
            <div className={styles.calloutHint}>
              {t('sign:representatives.capHint', { cap, companyName })}
            </div>
          </div>
        </div>

        <div className={styles.sectionHead}>
          <span className={styles.sectionTitle}>
            {t('sign:representatives.sectionTitle')}
          </span>
          <span className={styles.slotsPill}>
            {t('sign:representatives.slots', { used: rows.length, cap })}
          </span>
        </div>

        <div className={styles.cards}>
          {rows.map((row, index) => (
            <div className={styles.card} key={index}>
              <div className={styles.cardHead}>
                <span className={styles.cardAvatar} aria-hidden="true">
                  {getInitials(row.name) || index + 1}
                </span>
                <span className={styles.cardLabel}>
                  {t('sign:representatives.cardLabel', { index: index + 1 })}
                </span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    className={styles.cardRemove}
                    aria-label={t('sign:representatives.remove')}
                    onClick={() => removeRow(index)}
                  >
                    <IcCross className={styles.cardRemoveIcon} />
                  </button>
                )}
              </div>
              <div className={styles.cardFields}>
                <div>
                  <FormInput
                    id={`rep-name-${index}`}
                    label={t('sign:representatives.fields.name')}
                    placeholder={t('auth:fields.namePlaceholder')}
                    value={row.name}
                    onChange={(e) => updateRow(index, { name: e.target.value })}
                  />
                </div>
                <div>
                  <FormInput
                    id={`rep-email-${index}`}
                    label={t('sign:representatives.fields.email')}
                    placeholder={t('auth:fields.emailPlaceholder')}
                    type="email"
                    value={row.email}
                    onChange={(e) =>
                      updateRow(index, { email: e.target.value })
                    }
                  />
                </div>
                {showPhone && (
                  <FormPhoneInput
                    className={styles.fieldWide}
                    id={`rep-phone-${index}`}
                    label={t('sign:representatives.fields.phone')}
                    value={row.phone}
                    onChange={(value) => updateRow(index, { phone: value })}
                    required={phoneRequired}
                    optional={!phoneRequired}
                  />
                )}
              </div>
            </div>
          ))}

          {rows.length < cap && (
            <div className={styles.addCard}>
              <button
                type="button"
                className={styles.addAction}
                onClick={addRow}
              >
                <IcPlus className={styles.addActionIcon} />
                {t('sign:representatives.addRepresentative')}
              </button>
              {!myselfAdded && (
                <button
                  type="button"
                  className={styles.addAction}
                  onClick={addMyself}
                >
                  <IcPlus className={styles.addActionIcon} />
                  {t('sign:representatives.addMyself')}
                </button>
              )}
            </div>
          )}
        </div>

        {formError && <FormError message={formError} isRoot />}

        <Button
          className={styles.submit}
          onClick={() => void handleSubmit()}
          disabled={!!loading}
        >
          {t('sign:representatives.submit', { count: rows.length })}
        </Button>
      </div>
    </LoginGuardShell>
  );
};

type SubmittedProps = {
  companyName: string;
  named: SignerResponseDto[];
};

const Submitted = ({ companyName, named }: SubmittedProps) => {
  const { t } = useTranslation(['sign']);

  return (
    <LoginGuardShell
      introKey="sign:representatives.submitted.intro"
      titleKey="sign:representatives.submitted.title"
    >
      <div className={styles.body}>
        <div className={styles.callout}>
          <IcInfoCircled className={styles.calloutIcon} aria-hidden="true" />
          <div>{t('sign:representatives.submitted.text', { companyName })}</div>
        </div>
        <ul className={styles.namedList}>
          {named.map((rep) => (
            <li className={styles.namedItem} key={rep.id}>
              <span className={styles.cardAvatar} aria-hidden="true">
                {getInitials(rep.name)}
              </span>
              <span className={styles.namedIdentity}>
                <span className={styles.namedName}>{rep.name}</span>
                <span className={styles.namedEmail}>{rep.email}</span>
              </span>
              <span className={styles.namedPill}>
                <span className={styles.namedPillDot} aria-hidden="true" />
                {t('sign:representatives.invited')}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </LoginGuardShell>
  );
};

export default Representatives;
